import { unitFieldUtils } from 'v4/shared/components/forms/input/unit-field-utils';
import type {
  FieldCodec,
  MeasurementValue
} from 'v5/core/entity-values/values.types';
import { customFieldColumns } from './customFieldColumns';
import { findCustomFieldRowForRead } from './customFieldRows';
import { validateField, validation } from './validation';

function isEmpty(value: MeasurementValue): boolean {
  return unitFieldUtils.isEmpty(value);
}

/**
 * MEASUREMENT_NEW custom field. Written like legacy `MEASUREMENT_NEW.jsx`:
 * the typed input in `raw_value` + `raw_unit`, from which the
 * `sync_measurement_fields` trigger derives feet in `float_value` and the
 * canonical `string_value: 'ft'`. The codec sends that derivation too, as the
 * trigger skips an update that leaves `raw_*` unchanged.
 *
 * Reads `float_value` in the display unit, else `raw_value` (the trigger
 * leaves `float_value` empty for input it cannot parse, e.g. `5'6.5"`).
 */
export const measurementCodec: FieldCodec<'measurement'> = {
  decode(entity, spec, ctx) {
    const row = findCustomFieldRowForRead(entity, spec.definition.id, ctx);
    if (row === null)
      return unitFieldUtils.getUnitValueFromImperial(null, spec.unit);
    const unit = unitFieldUtils.getMeasurementNewDisplayUnit(
      row,
      spec.definition
    );
    if (row.float_value == null && row.raw_value) {
      return unitFieldUtils.getUnitValueFromServerValue(row.raw_value, unit);
    }
    return unitFieldUtils.getUnitValueFromImperial(row.float_value, unit);
  },
  encode(value) {
    const empty = isEmpty(value);
    return {
      type: 'custom',
      columns: customFieldColumns({
        raw_value: empty
          ? null
          : unitFieldUtils.getServerValueFromUnitValue(value),
        raw_unit: value.unit,
        float_value: empty
          ? null
          : unitFieldUtils.getImperialValueFromUnitValue(value),
        string_value: empty ? null : 'ft'
      })
    };
  },
  empty(spec) {
    return unitFieldUtils.getUnitValueFromImperial(null, spec.unit);
  },
  isEmpty,
  validate(value, spec) {
    return validateField(value, spec, isEmpty, value =>
      !isEmpty(value) &&
      unitFieldUtils.getImperialValueFromUnitValue(value) === null
        ? validation.invalid({ code: 'not-a-number' })
        : validation.valid()
    );
  }
};
